"use client";

import { ComponentProps, useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";

import { cn } from "@/lib/utils";

gsap.registerPlugin(Flip, useGSAP);

type FlipRevealItemProps = {
  flipKey: string;
} & ComponentProps<"div">;

export const FlipRevealItem = ({ flipKey, className, ...props }: FlipRevealItemProps) => {
  return <div data-flip={flipKey} className={className} {...props} />;
};

type FlipRevealProps = {
  keys: string[];
  showClass?: string;
  hideClass?: string;
} & ComponentProps<"div">;

export const FlipReveal = ({
  keys,
  hideClass = "hidden",
  showClass = "flex",
  className,
  children,
  ...props
}: FlipRevealProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isShow = (key: string | null) => !!key && (keys.includes("all") || keys.includes(key));

  useGSAP(
    () => {
      if (!wrapperRef.current) return;

      const items = gsap.utils.toArray<HTMLElement>("[data-flip]", wrapperRef.current);
      const state = Flip.getState(items);

      items.forEach((item) => {
        const key = item.getAttribute("data-flip");
        if (isShow(key)) {
          if (showClass) item.classList.add(...showClass.split(" ").filter(Boolean));
          if (hideClass) item.classList.remove(...hideClass.split(" ").filter(Boolean));
        } else {
          if (showClass) item.classList.remove(...showClass.split(" ").filter(Boolean));
          if (hideClass) item.classList.add(...hideClass.split(" ").filter(Boolean));
        }
      });

      Flip.from(state, {
        duration: 0.6,
        scale: true,
        ease: "power1.inOut",
        stagger: 0.05,
        absolute: true,
        onEnter: (elements) =>
          gsap.fromTo(
            elements,
            { opacity: 0, scale: 0 },
            { opacity: 1, scale: 1, duration: 0.6 },
          ),
        onLeave: (elements) =>
          gsap.to(elements, { opacity: 0, scale: 0, duration: 0.6 }),
      });
    },
    { scope: wrapperRef, dependencies: [keys.join(",")] },
  );

  return <div ref={wrapperRef} className={cn(className)} {...props}>{children}</div>;
};
